class BarikiProductForm {
  constructor(root) {
    this.root = root;
    this.variantSelect = root.querySelector('[data-variant-select]');
    this.priceRoot = root.querySelector('[data-product-price]');
    this.addToCartButton = root.querySelector('[data-add-to-cart]');
    this.enquiryButton = root.querySelector('[data-enquiry-link]');
    this.featuredMedia = root.querySelector('[data-featured-media]');
    this.thumbs = root.querySelectorAll('[data-product-thumb]');
    this.data = this.getProductData();

    this.mediaIds = Array.from(this.featuredMedia ? this.featuredMedia.querySelectorAll('[data-media-id]') : []).map(el => el.dataset.mediaId);

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.activateMedia(thumb.dataset.mediaId));
    });

    this.initGalleryArrows();
    this.initSwipe();
    this.initLightbox();
    this.initMobileScrollSync();

    if (!this.variantSelect || !this.data) return;

    this.variantSelect.addEventListener('change', () => this.onVariantChange());
    this.onVariantChange();
  }

  getProductData() {
    const script = this.root.querySelector('[data-product-json]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  formatMoney(cents) {
    const value = (cents || 0) / 100;
    const formatted = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: this.root.dataset.currency || document.body.dataset.currency || 'AUD',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
    return formatted;
  }

  onVariantChange() {
    const variantId = Number(this.variantSelect.value);
    const variant = this.data.variants.find((item) => item.id === variantId);
    if (!variant) return;

    if (this.priceRoot) {
      const suffix = this.priceRoot.querySelector('.woocommerce-price-suffix');
      const suffixHtml = suffix ? suffix.outerHTML : '';
      let priceHtml;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = '<del aria-hidden="true">' + this.formatMoney(variant.compare_at_price) + '</del>'
          + ' <ins aria-hidden="true">' + this.formatMoney(variant.price) + '</ins>';
      } else {
        priceHtml = this.formatMoney(variant.price);
      }
      this.priceRoot.innerHTML = priceHtml + '\n' + suffixHtml;
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = !variant.available;
      this.addToCartButton.textContent = variant.available
        ? this.addToCartButton.dataset.availableLabel
        : this.addToCartButton.dataset.unavailableLabel;
    }

    if (this.enquiryButton) {
      const url = new URL(this.enquiryButton.dataset.baseHref, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      url.searchParams.set('variant_title', variant.title);
      this.enquiryButton.href = url.toString();
    }

    if (variant.featured_media) {
      this.activateMedia(String(variant.featured_media.id));
    }
  }

  initGalleryArrows() {
    if (!this.featuredMedia || this.mediaIds.length < 2) return;
    const prev = this.featuredMedia.querySelector('[data-gallery-prev]');
    const next = this.featuredMedia.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', (e) => { e.preventDefault(); this.navigateGallery(-1); });
    if (next) next.addEventListener('click', (e) => { e.preventDefault(); this.navigateGallery(1); });
  }

  navigateGallery(direction) {
    const current = this.featuredMedia.querySelector('[data-media-id]:not([style*="display:none"]):not([style*="display: none"])');
    if (!current) return;
    const idx = this.mediaIds.indexOf(current.dataset.mediaId);
    const next = (idx + direction + this.mediaIds.length) % this.mediaIds.length;
    this.activateMedia(this.mediaIds[next]);
  }

  // Mobile: when the user free-scrolls the snap carousel, update the active
  // dot/thumb to match whichever image is currently visible.
  initMobileScrollSync() {
    if (!this.featuredMedia || this.mediaIds.length < 2 || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (!window.matchMedia('(max-width: 768px)').matches) return;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          const id = entry.target.dataset.mediaId;
          if (id) {
            this.thumbs.forEach((thumb) => {
              thumb.classList.toggle('is-active', thumb.dataset.mediaId === id);
            });
          }
        }
      });
    }, { root: this.featuredMedia, threshold: [0.6] });

    this.featuredMedia.querySelectorAll('[data-media-id]').forEach((el) => observer.observe(el));
  }

  // Touch swipe — left = next image, right = previous. Desktop only;
  // on mobile (≤768px) the gallery is a native horizontal scroll-snap carousel
  // so the browser handles swipes natively.
  initSwipe() {
    if (!this.featuredMedia || this.mediaIds.length < 2) return;
    if (window.matchMedia('(max-width: 768px)').matches) return;
    let startX = 0, startY = 0, startT = 0;
    const SWIPE_MIN = 40;
    const VERTICAL_TOLERANCE = 0.6;

    this.featuredMedia.addEventListener('touchstart', (e) => {
      if (!e.touches || e.touches.length !== 1) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startT = Date.now();
    }, { passive: true });

    this.featuredMedia.addEventListener('touchend', (e) => {
      if (!startT) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      startT = 0;
      if (Math.abs(dy) > Math.abs(dx) * VERTICAL_TOLERANCE) return;
      if (Math.abs(dx) < SWIPE_MIN) return;
      this.navigateGallery(dx < 0 ? 1 : -1);
    }, { passive: true });
  }

  initLightbox() {
    if (!this.featuredMedia) return;

    // Prevent default link navigation on gallery images
    this.featuredMedia.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      e.preventDefault();
      this.openLightbox(link.href);
    });

    // Hover zoom effect
    this.featuredMedia.querySelectorAll('[data-media-id]').forEach((container) => {
      this.initHoverZoom(container);
    });
  }

  initHoverZoom(container) {
    const link = container.querySelector('a');
    if (!link) return;

    const zoomImg = document.createElement('img');
    zoomImg.className = 'product-zoom-img';
    zoomImg.src = link.href;
    zoomImg.alt = '';
    link.style.position = 'relative';
    link.style.overflow = 'hidden';
    link.style.display = 'block';
    link.appendChild(zoomImg);

    link.addEventListener('mouseenter', () => {
      zoomImg.style.opacity = '1';
    });

    link.addEventListener('mouseleave', () => {
      zoomImg.style.opacity = '0';
    });

    link.addEventListener('mousemove', (e) => {
      const rect = link.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      zoomImg.style.transformOrigin = x + '% ' + y + '%';
    });
  }

  openLightbox(src) {
    // Hover-zoom can be left in opacity:1 if the user clicked through it —
    // the mouseleave fires too late or never, leaving a broken zoom layer
    // visible after the lightbox closes. Force-hide all zoom overlays now
    // and re-hide on close.
    const resetHoverZoom = () => {
      document.querySelectorAll('.product-zoom-img').forEach((img) => {
        img.style.opacity = '0';
      });
    };
    resetHoverZoom();

    const overlay = document.createElement('div');
    overlay.className = 'product-lightbox';
    overlay.innerHTML = `
      <button class="product-lightbox__close" aria-label="Close">&times;</button>
      <img src="${src}" alt="" class="product-lightbox__img">
    `;

    const close = () => {
      overlay.remove();
      resetHoverZoom();
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('.product-lightbox__close')) {
        close();
      }
    });

    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);

    document.body.style.overflow = 'hidden';
    document.body.appendChild(overlay);
  }

  activateMedia(mediaId) {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (this.featuredMedia) {
      const items = this.featuredMedia.querySelectorAll('[data-media-id]');
      if (isMobile) {
        // Mobile: items are laid out as a horizontal scroll-snap carousel.
        // Scroll only the gallery container, NOT the page. scrollIntoView
        // would walk up the ancestor chain and scroll any scrollable parent.
        const target = this.featuredMedia.querySelector('[data-media-id="' + mediaId + '"]');
        if (target) {
          const left = target.offsetLeft - this.featuredMedia.offsetLeft;
          this.featuredMedia.scrollTo({ left, behavior: 'smooth' });
        }
      } else {
        items.forEach((el) => {
          el.style.display = el.dataset.mediaId === mediaId ? '' : 'none';
        });
      }
    }

    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
    });
  }
}

window.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-root]').forEach((root) => {
    new BarikiProductForm(root);
  });
});
